import { useState } from "react";
import { JsonField } from "react-admin-json-view";
import { ShowContextProvider, Title, useEditController } from "react-admin";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { IdChip } from "../../components/IdChip";
import { colors, fontFamilyMono, radius } from "../../data/tokens";
import {
  MASK,
  displayValue,
  isPlainObject,
  isSecretKey,
  settingMeta,
} from "./system-settings-util";

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.textMuted,
};

// Ficha da configuração: nome amigável + para que serve, campos
// rotulados com os SEGREDOS mascarados por padrão (a versão antiga
// imprimia a senha do SMTP em texto puro) e o JSON bruto como
// alternativa quando o valor não é um objeto simples.
export const SystemSettingShow = () => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "system-settings/crud",
    id,
  });
  const record = controller?.record;
  const [revealed, setRevealed] = useState(false);

  const meta = settingMeta(record?.name);
  const flat = record && isPlainObject(record.value);
  const hasSecrets =
    flat && Object.keys(record.value).some((key) => isSecretKey(key));

  return (
    <ShowContextProvider value={controller}>
      <Title title="Configurações de Sistema" />
      <PageTopBreadcrumb
        title="Configurações de Sistema"
        firstPath="/#/system-settings/crud"
        secondTitle="Visualizando"
        thirdTitle={record?.name}
        actionType="edit"
      />
      {record && (
        <Card sx={{ marginBottom: 3 }}>
          {/* Box em vez de CardContent: o App.css zera o padding de todo
              .MuiCardContent-root com !important */}
          <Box sx={{ padding: { xs: 2.5, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  backgroundColor: colors.bgContent,
                  borderRadius: `${radius.sm}px`,
                  padding: 1,
                  display: "flex",
                  color: colors.primary,
                }}
              >
                <meta.Icon size={22} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: colors.primaryDark,
                  }}
                >
                  {meta.label}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: colors.textMuted }}>
                  {meta.description}
                </Typography>
              </Box>
              {hasSecrets && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    revealed ? (
                      <HiOutlineEyeOff size={16} />
                    ) : (
                      <HiOutlineEye size={16} />
                    )
                  }
                  onClick={() => setRevealed((v) => !v)}
                >
                  {revealed ? "Ocultar segredos" : "Mostrar segredos"}
                </Button>
              )}
            </Box>

            <Divider sx={{ marginY: 2.5 }} />

            {flat ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  alignItems: "flex-start",
                }}
              >
                {Object.entries(record.value).map(([key, value]) => (
                  <Box key={key}>
                    <Typography sx={labelSx}>{key}</Typography>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        color: colors.textStrong,
                        fontFamily: fontFamilyMono,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {isSecretKey(key) && !revealed
                        ? MASK
                        : displayValue(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              // Valor fora do formato objeto simples: JSON bruto, como era
              <JsonField source="value" label={false} />
            )}

            <Divider sx={{ marginY: 2.5 }} />

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography sx={labelSx}>Nome técnico</Typography>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    color: colors.textStrong,
                    fontFamily: fontFamilyMono,
                  }}
                >
                  {record.name}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Identificador técnico</Typography>
                <Box sx={{ marginTop: 0.5 }}>
                  <IdChip value={record.id} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      )}
    </ShowContextProvider>
  );
};
